// ─────────────────────────────────────────────────────────────────────────────
// ClockSettingsPanel — STAGE 2 · STEP 4 (D4): the settings-page clock UI.
//
// Writes the ONE stored setting — `timezone`, an IANA zone name (default
// Australia/Hobart) — through POST /api/rexy/settings; the backend clock
// (core/rexy_clock) reads it and serves every gate. The zone list comes from
// the browser's own IANA database (Intl.supportedValuesOf), the backend
// re-validates against zoneinfo — junk never lands.
//
// NO IP-GUESSING, EVER (the VPS is in Frankfurt): the only way this value
// changes is the user choosing it here. MarketClock is the DISPLAY TWIN of
// this contract — it renders the same stored zone; this panel is the writer.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useClockSettings } from '../../data/dashboard';

const MONO = REXY_FONT;

// DESKTOP ARRANGEMENT ONLY. The settings read/write, the IANA zone list and the
// rexy:timezone propagation moved to data/dashboard/useClockSettings.ts under
// the 2026-07-31 "one core, every window" ruling. The render is unchanged, save
// for the two places that previously inlined `setState('idle')` / the disabled
// predicate — both now come from the core so the phone control cannot diverge.

export default function ClockSettingsPanel() {
  const {
    current, serverNow, choice, setChoice, state, err, schedStatus, zones, save, canSave,
  } = useClockSettings();

  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ CLOCK · SETTINGS</div>
      <div style={styles.subtle}>
        the one stored setting — user-set IANA timezone · every gate reads it · never IP-guessed
      </div>
      {err && state !== 'error' && <div style={styles.loud}>not_available — {err}</div>}
      <div style={styles.row}>
        <span style={styles.label}>stored</span>
        <span style={styles.value}>{current ?? '…'}</span>
      </div>
      <div style={styles.row}>
        <span style={styles.label}>backend local now</span>
        <span style={styles.value}>{serverNow ? serverNow.replace('T', ' ').slice(0, 19) : '…'}</span>
      </div>
      <div style={styles.editRow}>
        {zones.length > 0 ? (
          <select
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            style={styles.select}
          >
            {!zones.includes(choice) && choice && <option value={choice}>{choice}</option>}
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        ) : (
          <input
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            placeholder="Australia/Hobart"
            style={styles.select}
          />
        )}
        <button
          onClick={save}
          disabled={!canSave}
          style={{
            ...styles.saveBtn,
            ...(!canSave ? styles.saveDisabled : {}),
          }}
        >
          {state === 'saving' ? 'SAVING…' : 'SET'}
        </button>
      </div>
      {state === 'saved' && (
        <div style={styles.ok}>
          saved — {schedStatus && schedStatus.startsWith('rescheduled')
            ? `live schedule + MarketClock now follow ${current}`
            : `stored; MarketClock follows ${current}`}
        </div>
      )}
      {/* A save whose live reschedule did not land is a VISIBLE failure — the
          gates would fire on the old zone (Ghost hold item 3), so say so. */}
      {schedStatus && !schedStatus.startsWith('rescheduled')
        && schedStatus !== 'scheduler_not_running' && (
        <div style={styles.loud}>live schedule NOT moved — {schedStatus}</div>
      )}
      {state === 'error' && <div style={styles.loud}>rejected — {err}</div>}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO, fontSize: 12, letterSpacing: 2,
    color: REXY_CONSOLE.primary, marginBottom: 10,
  },
  subtle: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText, marginBottom: 8 },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    padding: '3px 0', fontFamily: MONO,
  },
  label: { fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText },
  value: { fontSize: 10, color: REXY_CONSOLE.infText },
  editRow: { display: 'flex', gap: 6, marginTop: 8 },
  select: {
    flex: 1, fontFamily: MONO, fontSize: 10,
    color: REXY_CONSOLE.infText, background: REXY_CONSOLE.panel,
    border: `1px solid ${REXY_CONSOLE.divider}`, borderRadius: 4, padding: '4px 6px',
  },
  saveBtn: {
    fontFamily: MONO, fontSize: 9, letterSpacing: 1.5, cursor: 'pointer',
    color: REXY_CONSOLE.primary, background: REXY_CONSOLE.badgeOkBg,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`, borderRadius: 4, padding: '4px 12px',
    boxShadow: 'inset 0 1px 0 rgba(0,191,255,0.15), 0 2px 6px rgba(0,0,0,0.4)',
  },
  saveDisabled: { opacity: 0.4, cursor: 'default' },
  ok: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.primary, marginTop: 6 },
  loud: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.critical, padding: '4px 0' },
};
